import WithModel from '@Ravnur-Inc/core/WithModel';
import $t from '@Ravnur-Inc/l10n/$t';
import { showErrorNotification } from '@Ravnur-Inc/notifications/service';
import { computed, defineComponent, PropType, ref, shallowRef } from 'vue';
import { $processException } from '@/helpers/error-handler';
import humanFileSize from '@Ravnur-Inc/helpers/human-file-size';
import BaseAttachmentRepository from '@/repositories/base-attachment-repository';
import { Attachment, AttachmentDescription, AttachmentIdentifier } from '@/types/Attachment';
import HiddenFileSelector from '../file-input/hidden-file-selector';
import Spinner from '@Ravnur-Inc/core/ui-kit/spinner/spinner';
import modalService from '@/helpers/modal.service';

import './attachments.scss';

const CN = 'attachments';

export const ATTACHMENT_MAX_SIZE = 25 * 1000 * 1000; // 25 MB
export const AGENDA_AND_MINUTES_FORMAT = 'application/pdf';

const Attachments = defineComponent({
  props: {
    repository: { type: Object as PropType<BaseAttachmentRepository>, required: true },
    list: { type: Array as PropType<Attachment[]>, required: true },
    descriptions: { type: Array as PropType<AttachmentDescription[]>, required: true },
  },
  emits: {
    'update:list': (list: Attachment[]) => list instanceof Array,
  },
  setup(props, ctx) {
    const identifier = ref<AttachmentIdentifier | null>('agenda');
    const selector = shallowRef<typeof HiddenFileSelector | null>(null);
    const loading = ref(false);

    const current = computed(
      () => props.descriptions.find((val) => val.kind === identifier.value) || null
    );

    const identifiers = computed(() => props.descriptions);

    const onlyOneKind = computed(
      () => props.list.some((val) => val.kind === identifier.value) && !current.value?.isPlural
    );

    const attachmentOptions = computed(() =>
      identifiers.value.map((i) => ({ kind: i.kind, label: $t(`common`, `attachment__${i.kind}`) }))
    );

    const applyAttachment = (attachment: Attachment | null) => {
      const attachments = [...props.list];

      if (!attachment) {
        return;
      }

      if (onlyOneKind.value) {
        const index = attachments.findIndex((val) => val.kind === identifier.value);

        if (index !== -1) {
          attachments[index] = attachment;
        }
      } else {
        attachments.push(attachment);
      }

      ctx.emit('update:list', attachments);
    };

    const uploadAttachment = async (file: File) => {
      const description = current.value;

      if (!description) {
        return;
      }

      const allowedTypes = description.acceptTypes.split(',');
      const maxSize = description.maxSize || ATTACHMENT_MAX_SIZE;

      if (!allowedTypes.includes(file.type)) {
        showErrorNotification($t('common', 'attachment__wrong-format'));
        return;
      }

      if (file.size > maxSize) {
        const size = humanFileSize(maxSize);
        showErrorNotification($t('common', 'attachment__max-size', { size }));
        return;
      }

      loading.value = true;

      try {
        const operation = await props.repository.uploadAttachment(file, description.kind);
        applyAttachment(operation.data);
      } catch (e) {
        $processException(e);
      } finally {
        loading.value = false;

        if (selector.value) {
          selector.value.$el.value = '';
        }
      }
    };

    const removeAttachment = async (attachment: Attachment) => {
      if (!attachment || !attachment.id) {
        return;
      }

      loading.value = true;

      try {
        await props.repository.remove(attachment.id);

        // remove from list
        const attachments = props.list.filter((val) => val.id !== attachment.id);
        ctx.emit('update:list', attachments);
      } catch (e) {
        $processException(e);
      } finally {
        loading.value = false;
      }
    };

    const openFileSelector = async () => {
      if (onlyOneKind.value) {
        await modalService.confirm($t('common', 'attachment__no-plural'), {
          closeLabel: '',
          confirmText: $t('common', 'action__ok'),
        });
      }

      if (selector.value && selector.value.$el instanceof HTMLElement) {
        selector.value.$el.click();
      }
    };

    const downloadAttachment = async (attachment: Attachment) => {
      const link = document.createElement('a');

      link.href = attachment.url;
      link.download = 'true';
      link.target = '_blank';
      link.click();
    };

    return () => (
      <div class={CN}>
        <div class={`${CN}__upload`}>
          <chosen
            class={`${CN}__selector`}
            keyForLabel="label"
            keyForValue="kind"
            options={attachmentOptions.value}
            vModel={[identifier.value, 'value']}
          />
          <HiddenFileSelector
            ref={selector}
            accept={current.value?.acceptTypes || 'application/pdf'}
            class={`${CN}__field`}
            onFileSelected={uploadAttachment}
          />

          <r-button
            class={`${CN}__select-btn`}
            color="primary"
            icon="upload"
            onClick={openFileSelector}
          >
            <l10n group="common" tkey="attachment__upload-btn" />
          </r-button>
        </div>
        {loading.value && <Spinner />}
        {props.list.length > 0 && (
          <div class={`${CN}__selected-wrapper`}>
            <table cellspacing="0" class={`${CN}__selected`}>
              <thead>
                <tr>
                  <th>Label</th>
                  <th>File name</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {props.list
                  .sort((a, b) => a.kind.localeCompare(b.kind))
                  .map((val, idx) => {
                    return (
                      <tr key={idx}>
                        <td>
                          <l10n group="common" tkey={`attachment__${val?.kind}`} />
                        </td>
                        <td class={`${CN}__download`} onClick={() => downloadAttachment(val)}>
                          {val?.originalFileName}
                        </td>
                        <td>
                          <r-button
                            color="primary"
                            icon="delete"
                            mode="frameless"
                            onClick={() => removeAttachment(val)}
                          />
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  },
});

// eslint-disable-next-line
// @ts-ignore
export default WithModel(Attachments, 'list');
